import { useEffect, useState } from 'react';
import Confirm from '../confirm';
import style from './style.module.scss';
import { useStore } from '../../lib/hooks/useStore';
import { observer } from 'mobx-react-lite';
import AccountCard from '../account-card';
import GenericButton from '../button-generic';
import { ICartItem } from '../../lib/types/CartItem';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import { AccountServerProps } from '../../lib/typegen/account-server-props';
import ScrollBar from '../scroll-bar';
import AccountNotFound from '../account-not-found';
import {
	accountSelectionViewStateRef,
	AccountSelectionViewType,
	resetAccountSelectionViewState,
} from '../../lib/helpers/accountSelectionViewState';

type AccountSelectionProps = AccountServerProps;

const ViewType = AccountSelectionViewType;
type ViewType = AccountSelectionViewType;

const AccountSelection = observer(function AccountSelection({
	accountCardServerProps,
	singleAccountConfirmProps,
	notFoundConfirmProps,
	singleAccountView,
	selectView,
	notFoundView,
	confirmView,
	nextPageTarget,
	previousPageTarget,
}: AccountSelectionProps) {
	const {
		accountStore: {
			lookupItems,
			clearLastLookupResponse,
			cartItems,
			addToCart,
			removeFromCart,
			focusedCartItem,
			setFocusedAndSelectedCartItemId,
		},
		configStore: { getString },
		sessionStore: { startEndSessionTasks },
		uiStore: {
			handleNavigation,
			setTitle,
			setShowCart,
			setCustomBackAction,
			clearCustomBackAction,
			overrideConfirmView,
			setOverrideConfirmView,
		},
	} = useStore();
	const [currentViewType, setCurrentViewType] = useState<ViewType>(() => {
		// Empty latest lookup must not restore Single/Select/Confirm from a prior visit.
		// Confirm is only restored when returning to cart (overrideConfirmView false + cart items).
		if (lookupItems.length === 0) {
			if (
				accountSelectionViewStateRef.current === ViewType.Confirm &&
				!overrideConfirmView &&
				cartItems.length > 0
			) {
				return ViewType.Confirm;
			}
			return ViewType.NotFound;
		}
		return accountSelectionViewStateRef.current;
	});

	useEffect(() => {
		const handleGoBack = () => {
			switch (currentViewType) {
				case ViewType.Select:
				case ViewType.Single:
					handleNavigation(previousPageTarget);
					break;
				case ViewType.Confirm:
					setCurrentViewType(ViewType.Select);
					break;
			}
		};

		setCustomBackAction(handleGoBack);

		return () => clearCustomBackAction();
	}, [clearCustomBackAction, currentViewType, handleNavigation, previousPageTarget, setCustomBackAction]);

	const handleDeleteClick = (id: string) => {
		setFocusedAndSelectedCartItemId(id);
		setCurrentViewType(ViewType.Delete);
	};

	useEffect(() => {
		switch (currentViewType) {
			case ViewType.Single:
				setTitle(getString(singleAccountView.title));
				setShowCart(false);
				break;
			case ViewType.Select:
				setTitle(getString(selectView.title));
				setShowCart(false);
				break;
			case ViewType.Delete:
				setTitle(getString('remove-from-cart-title'));
				setShowCart(true);
				break;
			case ViewType.NotFound:
				setTitle(getString('address-lookup'));
				setShowCart(false);
				break;
			case ViewType.Confirm:
				setTitle(getString(confirmView.title));
				setShowCart(true);
				break;
		}
	}, [currentViewType, selectView.title, confirmView.title, setTitle, singleAccountView.title, notFoundView.title, getString, setShowCart]);

	// Determine view type based on lookup items
	useEffect(() => {
		if (currentViewType !== ViewType.Delete) {
			// If there is one account, show single view
			if (lookupItems?.length === 1 && !cartItems.some((item) => item.id === lookupItems[0].id)) {
				setCurrentViewType(ViewType.Single);
			}
			// If there are multiple accounts, show select view
			else if (lookupItems?.length && lookupItems.length > 1 && (currentViewType !== ViewType.Confirm || overrideConfirmView)) {
				setCurrentViewType(ViewType.Select);
			}
			// Empty lookup: force NotFound (overrideConfirmView breaks Confirm protection after a new lookup)
			else if (lookupItems?.length === 0 && currentViewType != null && (currentViewType !== ViewType.Confirm || overrideConfirmView)) {
				setCurrentViewType(ViewType.NotFound);
			}
		}
		// Persist view state
		accountSelectionViewStateRef.current = currentViewType;
	}, [currentViewType, lookupItems, cartItems, overrideConfirmView]);

	const scrollAccountsUp = () => {
		const accountsEl = document.querySelector(`.${style['form__accounts']}`);
		if (accountsEl) {
			accountsEl.scrollTop -= 150;
		}
	};

	const scrollAccountsDown = () => {
		const accountsEl = document.querySelector(`.${style['form__accounts']}`);
		if (accountsEl) {
			accountsEl.scrollTop += 150;
		}
	};

	const handleCardClick = (id: string) => {
		if (currentViewType === ViewType.Select && !cartItems.some((cartItem) => cartItem.id === id)) {
			addToCart(id);
		} else if (currentViewType === ViewType.Select && cartItems.some((cartItem) => cartItem.id === id)) {
			removeFromCart(id);
		}
	};

	const handleCardHighlight = (item: ICartItem) => {
		return currentViewType === ViewType.Select && cartItems.some((cartItem) => cartItem.id === item.id);
	};

	const renderAccounts = (items: ICartItem[]) => (
		<div className={style['accounts-wrapper']}>
			<div className={style.form__accounts}>
				{items
					.filter((i) => i !== undefined)
					.map((item, i) => (
						<AccountCard
							{...accountCardServerProps}
							key={i}
							cartItem={item}
							showPastDue={currentViewType === ViewType.Select}
							showButton={currentViewType === ViewType.Confirm && cartItems.length > 1 ? 'delete' : undefined}
							onCardClick={handleCardClick}
							onDeleteClick={() => {
								handleDeleteClick(item.id);
							}}
							isHighlighted={handleCardHighlight(item)}
						/>
					))}
			</div>
			{items.length > 2 && <ScrollBar upAction={scrollAccountsUp} downAction={scrollAccountsDown} />}
		</div>
	);

	const renderSingleAccountView = () => {
		return (
			<div className={[style.view].join(' ')}>
				<article className={style.view__description}>{singleAccountView.description}</article>
				<div className={[style.view__form, style.form].join(' ')} role='listbox'>
					<AccountCard {...accountCardServerProps} cartItem={lookupItems[0]} showPastDue={true} isHighlighted={false} />
				</div>
				<Confirm
					{...singleAccountConfirmProps}
					className={style.view__confirm}
					yesCallback={() => {
						addToCart(lookupItems[0].id);
						if (cartItems.length == 0) {
							handleNavigation(nextPageTarget);
						} else {
							setCurrentViewType(ViewType.Confirm);
						}
					}}
					noCallback={() => {
						handleNavigation(previousPageTarget);
						clearLastLookupResponse();
					}}
				/>
			</div>
		);
	};

	const renderSelectView = () => {
		return (
			<div className={[style.view].join(' ')}>
				<article className={style.view__description}>{selectView.description ? getString(selectView.description) : ''}</article>
				<div className={[style.view__form, style.form].join(' ')} role='listbox'>
					{renderAccounts(lookupItems)}
					{
						<GenericButton
							className={style.view__action}
							type='button'
							icon={AppIconType.AcceptSolid}
							disabled={cartItems.length === 0}
							onClick={() => {
								setCurrentViewType(ViewType.Confirm);
								setOverrideConfirmView(false);
							}}
							innerText={getString('select')}
							hasGlow={true}
						/>
					}
				</div>
			</div>
		);
	};

	const renderConfirmView = () => {
		return (
			<div className={[style.view].join(' ')}>
				<article className={style.view__description}>{confirmView.description ? getString(confirmView.description) : ''}</article>
				<div className={[style.view__form, style.form].join(' ')} role='listbox'>
					{renderAccounts(cartItems)}
					{
						<GenericButton
							className={style.view__action}
							type='button'
							icon={AppIconType.CircleArrowRight}
							innerText={getString('next')}
							onClick={() => {
								handleNavigation(nextPageTarget);
							}}
							hasGlow={true}
						/>
					}
				</div>
			</div>
		);
	};

	const renderDeleteView = () => {
		return (
			<div className={[style.view].join(' ')}>
				<article className={style.view__description}>{singleAccountView.description}</article>
				<div className={[style.view__form, style.form].join(' ')} role='listbox'>
					<AccountCard {...accountCardServerProps} cartItem={focusedCartItem!} showPastDue={true} isHighlighted={false} />
				</div>
				{
					<Confirm
						titleLabel='remove-from-cart-description'
						yesButton={{ label: getString('yes'), value: 'yes', target: '' }}
						noButton={{ label: getString('no'), value: 'no', target: '' }}
						className={style.view__confirm}
						yesCallback={() => {
							removeFromCart(focusedCartItem!.id);
							setCurrentViewType(ViewType.Confirm);
						}}
						noCallback={() => {
							setCurrentViewType(ViewType.Confirm);
						}}
					/>
				}
			</div>
		);
	};

	const renderNotFoundView = () => {
		return (
			<AccountNotFound
				confirmProps={{
					previousPageTarget,
					questionLabel: notFoundConfirmProps.questionLabel,
					yesButtonLabel: notFoundConfirmProps.yesButton.label ?? 'yes',
					noButtonLabel: notFoundConfirmProps.noButton.label ?? 'no',
					yesCallback: () => {
						resetAccountSelectionViewState();
						setOverrideConfirmView(true);
						setCurrentViewType(ViewType.NotFound);
					},
					noCallback:
						cartItems.length === 0
							? async () => {
									await startEndSessionTasks();
								}
							: () => {
									setOverrideConfirmView(false);
									accountSelectionViewStateRef.current = ViewType.Confirm;
									setCurrentViewType(ViewType.Confirm);
								},
				}}
			/>
		);
	};

	const renderActiveView = () => {
		switch (currentViewType) {
			case ViewType.Single:
				if (lookupItems.length === 0) return renderNotFoundView();
				return renderSingleAccountView();
			case ViewType.Select:
				return renderSelectView();
			case ViewType.Confirm:
				return renderConfirmView();
			case ViewType.Delete:
				return renderDeleteView();
			case ViewType.NotFound:
			default:
				return renderNotFoundView();
		}
	};

	return <>{renderActiveView()}</>;
});

export default AccountSelection;
